import React, { forwardRef } from 'react';
import { Document, Page } from 'react-pdf';
import { UnfoldMore } from '@mui/icons-material';
import { UnfoldLayout } from '../../types/portfolio';

interface UnfoldableFlipPageProps {
  pdfUrl: string;
  width: number;
  layout: UnfoldLayout;
  onExpand: () => void;
  showUnfoldIcon: boolean;
}

/**
 * A flipbook page that can be expanded into a gatefold view
 * Shows actual PDF content with an unfold icon overlay on hover
 * Used for the second page of unfoldable spreads
 */
export const UnfoldableFlipPage = forwardRef<HTMLDivElement, UnfoldableFlipPageProps>(
  ({ pdfUrl, width, layout, onExpand, showUnfoldIcon }, ref) => {
    const layoutLabel = layout.replace('x', '×');

    const handleIconClick = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent page flip
      onExpand();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        onExpand();
      }
    };

    return (
      <div
        ref={ref}
        className="bg-white shadow-lg flex items-center justify-center overflow-hidden relative group"
      >
        <Document file={pdfUrl}>
          <Page
            pageNumber={1}
            width={width}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            loading={
              <div
                className="flex items-center justify-center"
                style={{ width, height: width * 1.414 }}
              >
                <span className="text-gray-500">Loading page...</span>
              </div>
            }
            error={
              <div
                className="flex items-center justify-center bg-gray-100"
                style={{ width, height: width * 1.414 }}
              >
                <span className="text-red-500">Error loading page</span>
              </div>
            }
          />
        </Document>

        {/* Unfold icon overlay - appears on hover */}
        {showUnfoldIcon && (
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 flex items-center justify-center pointer-events-none">
            <div
              className="bg-blue-500 rounded-full p-3 shadow-lg transform scale-0 group-hover:scale-100 transition-transform duration-200 cursor-pointer pointer-events-auto unfold-arrow-pulsate"
              onClick={handleIconClick}
              onKeyDown={handleKeyDown}
              role="button"
              tabIndex={0}
              aria-label={`Expand to view ${layout} layout with all pages`}
            >
              <UnfoldMore className="w-6 h-6 text-white" />
            </div>
          </div>
        )}

        {/* Layout badge - ALWAYS visible */}
        <div className="absolute top-3 right-3 bg-blue-500 text-white rounded-full px-2 py-1 shadow-md">
          <span className="text-xs font-semibold">{layoutLabel}</span>
        </div>
      </div>
    );
  }
);

UnfoldableFlipPage.displayName = 'UnfoldableFlipPage';
